import { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import toGeoJSON from '@mapbox/togeojson';
import { googleMapsApiKey } from './Constants';
import styles from './KMZViewer.module.css';

const KMZViewerGoogle = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const map = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [kmzData, setKmzData] = useState(null);
  const polygonsRef = useRef([]); // Google Maps polygon'larını saklamak için
  const mapIdRef = useRef('DEMO_MAP_ID'); // Map ID için

  useEffect(() => {
    // API key kontrolü
    if (!googleMapsApiKey || googleMapsApiKey === "" || googleMapsApiKey === "YOUR_GOOGLE_MAPS_API_KEY_HERE") {
      setError('Google Maps API key tanımlı değil! Lütfen .env dosyasına REACT_APP_GOOGLE_MAPS_API_KEY ekleyin.');
      return;
    }

    // Google Maps API script'ini dinamik olarak yükle (yeni Web Components API)
    const loadGoogleMapsAPI = () => {
      // Eğer API zaten yüklenmişse, direkt haritayı oluştur
      if (window.google && window.google.maps) {
        initMap();
        return;
      }

      // Script zaten eklenmişse bekle
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        waitForGoogleMaps();
        return;
      }

      // Yeni Google Maps Web Components API script'ini ekle
      // callback kullanmadan async loading (best practice)
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=maps,marker,geometry&v=beta&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('✅ Google Maps API yüklendi');
        // API yüklendikten sonra container'ın hazır olmasını bekle
        setTimeout(() => {
          if (mapRef.current) {
            initMap();
          } else {
            console.warn('⚠️ Map container henüz hazır değil, tekrar denenecek...');
            setTimeout(() => initMap(), 500);
          }
        }, 200);
      };
      script.onerror = () => {
        setError('Google Maps API yüklenemedi. Lütfen API key\'inizi kontrol edin.');
      };
      document.head.appendChild(script);
    };

    const waitForGoogleMaps = () => {
      let attempts = 0;
      const checkInterval = setInterval(() => {
        attempts++;
        if (window.google && window.google.maps) {
          clearInterval(checkInterval);
          initMap();
        } else if (attempts > 100) { // 10 saniye sonra timeout
          clearInterval(checkInterval);
          setError('Google Maps API yüklenemedi. Lütfen API key\'inizi kontrol edin.');
        }
      }, 100);
    };

    const initMap = () => {
      if (!window.google || !window.google.maps) {
        console.error('Google Maps API yüklenmedi!');
        setError('Google Maps API yüklenmedi. Lütfen API key\'i kontrol edin ve sayfayı yenileyin.');
        return;
      }

      // Map container'ın hazır olduğundan emin ol
      if (!mapRef.current) {
        console.error('Map container bulunamadı!');
        setError('Map container bulunamadı.');
        return;
      }

      console.log('🗺️ Google Maps haritası oluşturuluyor...');
      console.log('📍 Container:', mapRef.current);
      console.log('📏 Container boyutları:', {
        width: mapRef.current.offsetWidth,
        height: mapRef.current.offsetHeight
      });

      // Google Maps haritasını oluştur (JavaScript API ile - polygon'lar için gerekli)
      try {
        map.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: 39.96, lng: 32.82 }, // Ankara merkez
          zoom: 12,
          mapTypeId: 'roadmap',
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: true,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: true
          // mapId kaldırıldı - beta API'de sorun çıkarabilir
        });
        
        console.log('✅ Google Maps haritası başarıyla oluşturuldu');
      } catch (err) {
        console.error('❌ Harita oluşturulurken hata:', err);
        setError('Harita oluşturulurken hata: ' + err.message);
      }
    };

    loadGoogleMapsAPI();

    return () => {
      // Cleanup
      if (polygonsRef.current.length > 0) {
        polygonsRef.current.forEach(polygon => {
          polygon.setMap(null);
        });
        polygonsRef.current = [];
      }
    };
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.kmz')) {
      setError('Lütfen bir KMZ dosyası seçin.');
      return;
    }

    setUploadedFile(file);
    setError(null);
    setIsLoading(true);

    try {
      await processKMZFile(file);
    } catch (err) {
      console.error('KMZ işleme hatası:', err);
      setError('KMZ dosyası işlenirken bir hata oluştu: ' + err.message);
      setIsLoading(false);
    }
  };

  const processKMZFile = async (file) => {
    try {
      // Önceki polygon'ları temizle
      polygonsRef.current.forEach(polygon => {
        polygon.setMap(null);
      });
      polygonsRef.current = [];

      // KMZ dosyasını zip olarak aç
      const zip = await JSZip.loadAsync(file);
      
      // doc.kml dosyasını bul
      const kmlFile = zip.file('doc.kml');
      if (!kmlFile) {
        throw new Error('KMZ dosyasında doc.kml bulunamadı.');
      }

      const kmlContent = await kmlFile.async('string');
      const parser = new window.DOMParser();
      const kmlDoc = parser.parseFromString(kmlContent, 'text/xml');

      const parseError = kmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('KML dosyası parse edilemedi: ' + parseError.textContent);
      }

      // KML'den polygon'ları parse et
      const placemarks = kmlDoc.querySelectorAll('Placemark');
      const bounds = new window.google.maps.LatLngBounds();

      placemarks.forEach((pm) => {
        const polygon = pm.querySelector('Polygon');
        if (!polygon) return;

        // Outer boundary'yi bul
        const outerBoundary = polygon.querySelector('outerBoundaryIs') || 
                            polygon.querySelector('outerBoundary');
        if (!outerBoundary) return;

        const linearRing = outerBoundary.querySelector('LinearRing');
        if (!linearRing) return;

        const coordsElement = linearRing.querySelector('coordinates');
        if (!coordsElement) return;

        // Koordinatları parse et
        const coordText = coordsElement.textContent.trim();
        const coords = [];
        const lines = coordText.split(/[\s\n\r\t]+/).filter(line => line.trim().length > 0);

        lines.forEach(line => {
          const parts = line.trim().split(',');
          if (parts.length >= 2) {
            const lng = parseFloat(parts[0].trim());
            const lat = parseFloat(parts[1].trim());
            if (!isNaN(lng) && !isNaN(lat) && lng >= 25 && lng <= 45 && lat >= 35 && lat <= 43) {
              coords.push({ lat, lng });
              bounds.extend({ lat, lng });
            }
          }
        });

        if (coords.length < 3) return;

        // Polygon boyutunu kontrol et - çok büyük olanları filtrele
        const lngs = coords.map(c => c.lng);
        const lats = coords.map(c => c.lat);
        const lngRange = Math.max(...lngs) - Math.min(...lngs);
        const latRange = Math.max(...lats) - Math.min(...lats);
        
        // Çok büyük polygon'ları filtrele (0.2 derece = yaklaşık 20km)
        const maxRange = 0.2;
        if (lngRange > maxRange || latRange > maxRange) {
          console.log('🚫 Çok büyük polygon filtrelendi:', {
            name: pm.querySelector('name')?.textContent || 'İsimsiz',
            lngRange: lngRange.toFixed(4),
            latRange: latRange.toFixed(4)
          });
          return; // Çok büyük polygon'ları gösterme
        }

        // Style bilgisini al
        const styleUrlEl = pm.querySelector('styleUrl');
        const styleUrl = styleUrlEl ? styleUrlEl.textContent : '';
        const nameEl = pm.querySelector('name');
        const name = nameEl ? nameEl.textContent : 'İsimsiz';

        // Polygon tipini belirle (park, yol, diğer)
        const isPark = styleUrl.toLowerCase().includes('yesil') || 
                      styleUrl.toLowerCase().includes('green') ||
                      styleUrl.toLowerCase().includes('pl_ka') ||
                      name.toLowerCase().includes('park');
        
        const isRoad = styleUrl.toLowerCase().includes('z_ilce_pl0') ||
                     styleUrl.toLowerCase().includes('yol') ||
                     styleUrl.toLowerCase().includes('road');

        // Z_ILCE_PL0 polygon'ları genellikle çok büyük (ilçe sınırları) - filtrele
        if (styleUrl.toLowerCase().includes('z_ilce_pl0')) {
          // Z_ILCE_PL0 polygon'ları çok büyükse filtrele (0.15 derece = yaklaşık 15km)
          if (lngRange > 0.15 || latRange > 0.15) {
            console.log('🚫 Büyük Z_ILCE_PL0 polygon filtrelendi:', {
              name: name,
              lngRange: lngRange.toFixed(4),
              latRange: latRange.toFixed(4)
            });
            return; // Gösterme
          }
        }

        // Renkleri belirle
        let fillColor, strokeColor, fillOpacity, strokeWeight;
        
        if (isPark) {
          fillColor = '#22c55e'; // Yeşil
          strokeColor = '#ef4444'; // Kırmızı
          fillOpacity = 0.5;
          strokeWeight = 2;
        } else if (isRoad) {
          fillColor = '#3b82f6'; // Mavi
          strokeColor = '#1e40af'; // Koyu mavi
          fillOpacity = 0.6;
          strokeWeight = 2;
        } else {
          // Diğer polygon'lar - şeffaf
          return; // Gösterme
        }

        // Google Maps Polygon oluştur
        const googlePolygon = new window.google.maps.Polygon({
          paths: coords,
          strokeColor: strokeColor,
          strokeOpacity: 1.0,
          strokeWeight: strokeWeight,
          fillColor: fillColor,
          fillOpacity: fillOpacity,
          map: map.current
        });

        polygonsRef.current.push(googlePolygon);
      });

      // Haritayı tüm polygon'ları içerecek şekilde zoom yap
      if (polygonsRef.current.length > 0 && !bounds.isEmpty()) {
        map.current.fitBounds(bounds);
      }

      // GeoJSON data'yı sakla (info gösterimi için)
      const geoJsonData = {
        type: 'FeatureCollection',
        features: placemarks.length // Placemark sayısı
      };
      setKmzData(geoJsonData);

      setIsLoading(false);
    } catch (err) {
      console.error('KMZ işleme hatası:', err);
      throw err;
    }
  };

  const clearMap = () => {
    polygonsRef.current.forEach(polygon => {
      polygon.setMap(null);
    });
    polygonsRef.current = [];
    setUploadedFile(null);
    setKmzData(null);
    setError(null);
  };

  return (
    <div className={styles.kmzViewerContainer}>
      <div ref={mapRef} className={styles.kmzMap} id="kmz-map-google" />
      <div className={styles.kmzControls}>
        <h3>KMZ Viewer (Google Maps)</h3>
        <div className={styles.uploadSection}>
          <label htmlFor="kmz-file-input-google" className={styles.uploadButton}>
            {isLoading ? 'Yükleniyor...' : uploadedFile ? uploadedFile.name : 'KMZ Dosyası Seç'}
          </label>
          <input
            id="kmz-file-input-google"
            type="file"
            accept=".kmz"
            onChange={handleFileUpload}
            className={styles.fileInput}
            disabled={isLoading}
          />
          {uploadedFile && (
            <button onClick={clearMap} className={styles.clearButton}>
              Temizle
            </button>
          )}
        </div>
        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}
        {kmzData && (
          <div className={styles.infoSection}>
            <p><strong>Yüklenen:</strong> {uploadedFile?.name}</p>
            <p><strong>Feature Sayısı:</strong> {kmzData.features.length}</p>
          </div>
        )}
      </div>
      {/* Alt Navigasyon Menüsü */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        background: 'rgba(44, 62, 80, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '12px 20px',
        gap: '10px',
        flexWrap: 'wrap',
        alignItems: 'center',
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
        zIndex: 10000,
        maxWidth: '90%'
      }}>
        <button 
          onClick={() => navigate('/')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#34495e',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4a5f7a'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#34495e'}
        >
          🏠 Ana Sayfa
        </button>
        <button 
          onClick={() => navigate('/kmz')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#34495e',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4a5f7a'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#34495e'}
        >
          📍 KMZ Viewer
        </button>
        <button 
          onClick={() => navigate('/kmz-google')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#229954'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#27ae60'}
        >
          🗺️ Google API ile
        </button>
        <button 
          onClick={() => navigate('/test')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#34495e',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4a5f7a'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#34495e'}
        >
          🧪 Test Sayfası
        </button>
      </div>
    </div>
  );
};

export default KMZViewerGoogle;

